import Avatar from 'assets/icons/avatar.svg';
import exportIcon from 'assets/icons/export.svg';
import uploadButtonIcon from 'assets/icons/uploadButtonIcon.svg';
import UserIcon from 'assets/icons/userIcon.svg';
import gsmsLogo from 'assets/logo/gsmsLogo.png';
import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect } from 'react';
import BulkTmsUploadModal from 'screens/FactsScreen/components/marketStrategy/components/bulkTmsUploadValidationModal/BulkTmsUploadAndValidationModal';
import MenuPopOver from './components/menuPopover/index';
import NavbarLogic from './navbar.logic';
import classes from './navbar.module.scss';
import NotificationBell from './NotificationBell';
import NotificationPopup from './NotificationPopup';
import WebSocketStatusIndicator from './WebSocketStatusIndicator';

const Navbar = (props: {
  heading: string;
  searchComponent?: React.ReactNode;
  progressComponent?: React.ReactNode;
  bulkEditComponent?: React.ReactNode;
}) => {
  const [notificationPopupVisible, setNotificationPopupVisible] = React.useState(false);
  const notificationBellRef = React.useRef<HTMLDivElement>(null);
  const {
    NavbarStore: { isNavigationRestricted },
    WebSocketStore: { connectionStatus, reconnectAttempts, lastError },
    NotificationStore,
  } = useStore();

  const {
    handleNavigateToHomeScreen,
    isAuthenticated,
    userMetadata,
    logout,
    isUserInfoLoading,
    bulkTmsModalVisibility,
    toggleBulkTmsModalVisibility,
    isBulkUploadButtonVisible,
    isBulkEditButtonVisibleForMsSalesPlan,
    handleClickBulkEditButtonForMSSalesPlan,
    isProvisionalExportForecastButtonVisible,
    handleProvisonalForecastDownloadExcelFile,
    isProvisonalExportForecastButtonLoading,
    dropdownOptions,
    handleDropdownChange,
    selectedUserAction,
  } = NavbarLogic();

  useEffect(() => {
    if (isAuthenticated) {
      NotificationStore.fetchUnreadNotifications();
    }
  }, [isAuthenticated, NotificationStore]);

  // Value template - shows the avatar as the dropdown trigger
  const valueTemplate = () => {
    return (
      <div className={classes.userAvatarLogo}>
        <img src={Avatar} alt="Avatar" />
      </div>
    );
  };

  // Item template - shows the dropdown options
  const itemTemplate = (option: { type: string; label?: string }) => {
    if (option.type === 'userInfo') {
      return (
        <div className={classes.userDetails} style={{ display: 'flex', gap: '1rem' }}>
          <div className={classes.leftSide} style={{ display: 'flex', gap: '1rem' }}>
            <div className={classes.leftSideAvatar}>
              <img src={UserIcon} alt="avatar" style={{ cursor: 'pointer' }} />
            </div>
            {isUserInfoLoading ? (
              <Skeleton height="3rem" width="12rem" />
            ) : (
              <div className={classes.leftSideUseInfo}>
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userMetadata?.userInfo.name}
                </div>
                <div style={{ fontSize: '0.9rem' }}>{userMetadata?.userInfo.email}</div>
              </div>
            )}
          </div>
        </div>
      );
    }
    // else if (option.type === 'themeToggle') {
    //   return (
    //     <div
    //       style={{
    //         display: 'flex',
    //         alignItems: 'center',
    //         justifyContent: 'space-between',
    //         cursor: 'default',
    //       }}
    //       onClick={e => e.stopPropagation()}
    //     >
    //       <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    //         <span style={{ fontSize: '0.9rem' }}>🌙</span>
    //         <span style={{ fontSize: '0.9rem' }}>Dark Mode</span>
    //       </div>
    //       <InputSwitch
    //         checked={isDarkMode}
    //         onChange={e => handleThemeToggle(e.value)}
    //         style={{ transform: 'scale(0.8)' }}
    //       />
    //     </div>
    //   );
    // }
    else if (option.type === 'logout') {
      return (
        <div className={classes.rightSide}>
          <span
            className={classes.logoutText}
            style={{ cursor: 'pointer', fontSize: '1rem' }}
            onClick={logout}
          >
            Logout
          </span>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isNavigationRestricted) {
        event.preventDefault();
        event.returnValue = '';
      }
      return ''; //Return a string to show a message
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isNavigationRestricted]);

  const checkViewScreen = () => {
    const location = window.location.pathname;
    if (location === '/fdp/unconstrained_forecast' || location === '/fdp/constrained_forecast')
      return true;
    else return false;
  };

  return (
    <nav className={classes.navbar}>
      <div className={classes.navbarLeftSidebar}>
        <MenuPopOver />
        <img
          className={classes.logo}
          onClick={handleNavigateToHomeScreen}
          src={gsmsLogo}
          alt=""
          role="presentation"
        />
      </div>

      <div className={classes.navbarRightSidebar}>
        <div className={classes.bartitle}>{props.heading}</div>
        {isAuthenticated && props.searchComponent}
        {isBulkUploadButtonVisible() && (
          <Button
            outlined
            data-testid="bulkModalOpenButton"
            className={classes.bulkModalOpenButton}
            onClick={toggleBulkTmsModalVisibility}
            // disabled={isSearchButtonDisabled()}
          >
            <img src={uploadButtonIcon} alt="" /> <span>Bulk Upload</span>{' '}
          </Button>
        )}

        {isBulkEditButtonVisibleForMsSalesPlan() && (
          <Button
            outlined
            data-testid="bulkModalOpenButton"
            className={classes.bulkModalOpenButton}
            onClick={handleClickBulkEditButtonForMSSalesPlan}
          >
            <span>Bulk Edit</span>{' '}
          </Button>
        )}
        {isProvisionalExportForecastButtonVisible() && (
          <Button
            onClick={handleProvisonalForecastDownloadExcelFile}
            loading={isProvisonalExportForecastButtonLoading}
            className={classes.downloadProvisionalForecastExcelFileButton}
          >
            <img src={exportIcon} alt="export" style={{ transform: 'rotate(180deg)' }} />
            Export All
          </Button>
        )}
        {checkViewScreen() && <div>{props.bulkEditComponent}</div>}
        <div>{props.progressComponent}</div>
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
          }}
        >
          {/* <Button
            onClick={() => handleThemeToggle(!isDarkMode)}
            className={classes.themeToggleButton}
            data-testid="themeToggleButton"
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <i className="pi pi-moon"></i> : <i className="pi pi-sun"></i>}
          </Button> */}

          {isAuthenticated && (
            <WebSocketStatusIndicator
              status={connectionStatus}
              reconnectAttempts={reconnectAttempts}
              lastError={lastError}
            />
          )}

          {isAuthenticated && (
            <>
              <div ref={notificationBellRef}>
                <NotificationBell
                  unreadCount={NotificationStore.unreadCount}
                  onClick={() => setNotificationPopupVisible(!notificationPopupVisible)}
                />
              </div>
              <NotificationPopup
                target={notificationBellRef}
                visible={notificationPopupVisible}
                onHide={() => setNotificationPopupVisible(false)}
              />
            </>
          )}
          <Dropdown
            value={selectedUserAction}
            options={dropdownOptions}
            onChange={handleDropdownChange}
            optionLabel="label"
            pt={{
              trigger: {
                style: {
                  display: 'none',
                },
              },
            }}
            style={{ border: 'none' }}
            onFocus={e => e.target.blur()}
            valueTemplate={valueTemplate}
            itemTemplate={itemTemplate}
            placeholder="dropdown"
            className={classes.userDropdown}
            panelClassName={classes.userDropdownPanel}
            appendTo="self"
            showClear={false}
          />
        </div>
      </div>
      {bulkTmsModalVisibility && (
        <BulkTmsUploadModal
          visibility={bulkTmsModalVisibility}
          toggleVisibility={toggleBulkTmsModalVisibility}
        />
      )}
    </nav>
  );
};
export default observer(Navbar);
